import {AsyncInlineError, inlineError} from '@s-ui/decorators'
import {toQueryString} from '@s-ui/js/lib/string'

import {GetDraftInstantOfferFromAPISellerError} from '../Errors/GetDraftInstantOfferFromAPISellerError.js'
import {UpdateInstantOfferPhoneSellerError} from '../Errors/UpdateInstantOfferPhoneSellerError.js'
import {ConfirmAppointmentSellerValueObject} from '../ValueObjects/ConfirmAppointmentSellerValueObject.js'
import {ContactConfiguratorSellerResponseValueObject} from '../ValueObjects/ContactConfiguratorSellerResponseValueObject.js'
import {DraftInstantOfferFromAPISellerValueObject} from '../ValueObjects/DraftInstantOfferFromAPISellerValueObject.js'
import {GetLeadConfigurationResponseValueObject} from '../ValueObjects/GetLeadConfigurationResponseValueObject.js'
import {InstantOfferDetailsSellerValueObject} from '../ValueObjects/InstantOfferDetailsSellerValueObject.js'
import {SellerRepository} from './SellerRepository.js'

/**
 * @class
 * @implements SellerRepository
 */
export class HTTPSellerRepository extends SellerRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Fetcher} deps.authenticationFetcher,
   * @param {Config} deps.config
   * @param {Fetcher} deps.fetchOrFail,
   * @param {Mapper} deps.closeDealersResponseToVOMapper
   * @param {Mapper} deps.makeLeadResponseToContactModelSellerResponseVOMapper
   * @param {Function} deps.confirmAppointmentGenericSellerErrorFactory
   * @param {Function} deps.contactModelSellerErrorFactory
   * @param {Function} deps.getCloseDealersGenericErrorFactory
   * @param {Function} deps.getCloseDealersNotFoundErrorFactory
   * @param {Function} deps.getInstantOfferGenericSellerErrorFactory
   * @param {Function} deps.getInstantOfferNotFoundSellerErrorFactory
   * @param {Function} deps.getRecommendedPriceGenericSellerErrorFactory
   * @param {Function} deps.instantOfferSellerEntityFactory
   * @param {Function} deps.recommendedPriceVOFactory
   * @param {Function} deps.instantOfferSellerVOFactory
   */
  constructor({
    authenticationFetcher,
    config,
    fetchOrFail,
    makeLeadResponseToContactModelSellerResponseVOMapper,
    closeDealersResponseToVOMapper,
    confirmAppointmentGenericSellerErrorFactory,
    contactModelSellerErrorFactory,
    getCloseDealersGenericErrorFactory,
    getCloseDealersNotFoundErrorFactory,
    getInstantOfferDetailsGenericSellerErrorFactory,
    getInstantOfferDetailsNotFoundSellerErrorFactory,
    getInstantOfferGenericSellerErrorFactory,
    getInstantOfferNotFoundSellerErrorFactory,
    getRecommendedPriceGenericSellerErrorFactory,
    instantOfferSellerEntityFactory,
    recommendedPriceVOFactory,
    instantOfferSellerVOFactory
  }) {
    super()

    this._authenticationFetcher = authenticationFetcher
    this._config = config
    this._fetchOrFail = fetchOrFail
    this._makeLeadResponseToContactModelSellerResponseVOMapper = makeLeadResponseToContactModelSellerResponseVOMapper
    this._closeDealersResponseToVOMapper = closeDealersResponseToVOMapper
    this._confirmAppointmentGenericSellerErrorFactory = confirmAppointmentGenericSellerErrorFactory
    this._contactModelSellerErrorFactory = contactModelSellerErrorFactory
    this._getCloseDealersGenericErrorFactory = getCloseDealersGenericErrorFactory
    this._getCloseDealersNotFoundErrorFactory = getCloseDealersNotFoundErrorFactory
    this._getInstantOfferDetailsGenericSellerErrorFactory = getInstantOfferDetailsGenericSellerErrorFactory
    this._getInstantOfferDetailsNotFoundSellerErrorFactory = getInstantOfferDetailsNotFoundSellerErrorFactory
    this._getInstantOfferGenericSellerErrorFactory = getInstantOfferGenericSellerErrorFactory
    this._getInstantOfferNotFoundSellerErrorFactory = getInstantOfferNotFoundSellerErrorFactory
    this._getRecommendedPriceGenericSellerErrorFactory = getRecommendedPriceGenericSellerErrorFactory
    this._instantOfferSellerEntityFactory = instantOfferSellerEntityFactory
    this._recommendedPriceVOFactory = recommendedPriceVOFactory
    this._instantOfferSellerVOFactory = instantOfferSellerVOFactory

    this._getRequestHeaders = ({captchaToken}) => {
      const {CAPTCHA_TOKEN} = this._config.get('REQUEST_HEADERS')
      return {
        [CAPTCHA_TOKEN]: captchaToken
      }
    }
  }

  /**
   * @param {Object} params
   * @param {instantOfferEntity} params.instantOfferEntity
   * @return {Promise<InstantOfferDetailsSellerValueObject>}
   */
  @inlineError
  getInstantOfferDetails({instantOfferEntity}) {
    const instantOfferId = instantOfferEntity.getId()

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_REQUEST, NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const url = `${this._config.get('API_URL')}/instant-offers/${instantOfferId}`

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => this._getInstantOfferDetailsGenericSellerErrorFactory(message),
        [BAD_REQUEST]: ({message}) => this._getInstantOfferDetailsGenericSellerErrorFactory(message),
        [NOT_FOUND]: ({message}) => this._getInstantOfferDetailsNotFoundSellerErrorFactory(message)
      })
      .get(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        return InstantOfferDetailsSellerValueObject.createFromApi(response.data)
      })
  }

  /**
   * @param {Object} params
   * @param {appointmentSlotValueObject} params.appointmentVO
   * @param {dealerEntity} params.dealerEntity
   * @param {instantOfferEntity} params.instantOfferEntity
   * @param {Boolean} params.isNextAppointment
   * @return {ConfirmAppointmentSellerValueObject}
   */
  @inlineError
  confirmAppointmentWithDealer({appointmentVO, dealerEntity, instantOfferEntity, isNextAppointment}) {
    const dealerId = dealerEntity.getId()
    const appointmentSlot = appointmentVO.getAppointment()
    const instantOfferId = instantOfferEntity.getId()

    const url = `${this._config.get(
      'API_URL'
    )}/v2/instant-offers/${instantOfferId}/appointment?nextAppointment=${isNextAppointment}`

    const body = {dealerId, appointment: appointmentSlot}

    const {GENERIC} = this._config.get('COMMON').ERRORS

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._confirmAppointmentGenericSellerErrorFactory(
            '[HTTPSellerRepository#confirmAppointmentGenericSellerError]'
          )
        }
      })
      .post(url, body)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)

        const {appointmentId, dealerId, appointment} = response.data || {}
        return ConfirmAppointmentSellerValueObject.createFromApi({appointmentId, dealerId, appointment})
      })
  }

  /**
   * @return {Promise<ContactModelSellerResponseValueObject, Error>}
   */
  @inlineError
  async contactModelSeller(contactModelSellerRequest) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const endpoint = `${this._config.get('SECURED_API_URL')}/v1/makelead`
    const {captchaToken, ...contactModelSellerRequestJSON} = contactModelSellerRequest.toJSON()
    const headers = this._getRequestHeaders({captchaToken})

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._contactModelSellerErrorFactory(message)
        }
      })
      .post(endpoint, contactModelSellerRequestJSON, {headers})

    if (error) throw error

    const {data} = response
    return this._makeLeadResponseToContactModelSellerResponseVOMapper.map(data)
  }

  /**
   * @param {Object} params
   * @param {getCloseDealersRequest} params.getCloseDealersRequest
   * @return {Promise}
   */
  @inlineError
  getCloseDealers({getCloseDealersRequest}) {
    const ioId = getCloseDealersRequest.getInstantOfferId()
    const isNextAppointment = getCloseDealersRequest.isNextAppointment()

    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const url = isNextAppointment
      ? `${this._config.get('API_URL')}/v2/instant-offers/${ioId}/dealers/next-appointment?dealer_type=all`
      : `${this._config.get('API_URL')}/v2/instant-offers/${ioId}/dealers?dealer_type=all`

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => this._getCloseDealersGenericErrorFactory(message),
        [NOT_FOUND]: ({message}) => this._getCloseDealersNotFoundErrorFactory(message)
      })
      .get(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        if (response?.status === 204)
          return Promise.reject(
            this._getCloseDealersNotFoundErrorFactory('[HTTPSellerRepository#getInstantOfferNotFoundError]')
          )

        const closeDealerListVO = this._closeDealersResponseToVOMapper.map(response.data)

        if (closeDealerListVO.getList().length === 0) {
          return Promise.reject(
            this._getCloseDealersNotFoundErrorFactory('[HTTPSellerRepository#closeDealersNotFound]')
          )
        }

        return closeDealerListVO
      })
  }

  @inlineError
  getSimulatedInstantOffer({getSimulatedInstantOfferRequest}) {
    const url = `${this._config.get('API_URL')}/v2/instant-offers/appraisal-simulation`

    const body = getSimulatedInstantOfferRequest.getRequest()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getInstantOfferGenericSellerErrorFactory(
            '[HTTPSellerRepository#getInstantOfferGenericSellerError]'
          )
        }
      })
      .post(url, body)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        if (response?.status === 204)
          return Promise.reject(
            this._getInstantOfferGenericSellerErrorFactory('[HTTPSellerRepository#getInstantOfferGenericSellerError]')
          )
        return this._instantOfferSellerVOFactory({
          ...response.data
        })
      })
  }

  @inlineError
  getInstantOffer({getInstantOfferRequest, userId}) {
    const queryParams = toQueryString({unique_price: true, user_id: userId || undefined})
    const url = `${this._config.get('API_URL')}/v2/instant-offers/appraisal?${queryParams}`

    const body = getInstantOfferRequest.getRequest()
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_GATEWAY, BAD_REQUEST, NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () =>
          this._getInstantOfferGenericSellerErrorFactory('[HTTPSellerRepository#getInstantOfferGenericSellerError]'),
        [BAD_REQUEST]: () =>
          this._getInstantOfferGenericSellerErrorFactory('[HTTPSellerRepository#getInstantOfferGenericSellerError]'),
        [BAD_GATEWAY]: () =>
          this._getInstantOfferGenericSellerErrorFactory('[HTTPSellerRepository#getInstantOfferGenericSellerError]')
      })
      .post(url, body)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        if (response?.status === NO_CONTENT)
          return Promise.reject(
            this._getInstantOfferNotFoundSellerErrorFactory('[HTTPSellerRepository#getInstantOfferNotFoundError]')
          )

        const {
          bodyTypeDoors,
          fuelId,
          makeId,
          makeName,
          mileage,
          modelId,
          modelName,
          licensePlate,
          transmissionTypeId,
          versionId,
          versionName,
          year
        } = body

        return this._instantOfferSellerEntityFactory({
          ...response.data,
          postalCode: body.postalCode.toString().padStart(5, '0'),
          vehicle: {
            bodyTypeDoors,
            fuelId: fuelId.toString(),
            km: mileage.toString(),
            makeId: makeId.toString(),
            makeName,
            modelId: modelId.toString(),
            modelName,
            carPlate: licensePlate,
            transmissionTypeId: transmissionTypeId.toString(),
            versionId: versionId.toString(),
            versionName,
            year: year.toString()
          }
        })
      })
  }

  /**
   * @param {Object} params
   * @param {GetRecommendedPriceRequest} params.recommendedPriceRequest
   * @return {Promise}
   */
  @inlineError
  getRecommendedPrice({recommendedPriceRequest}) {
    const apiUrl = this._config.get('API_URL')
    const queryParams = recommendedPriceRequest.getQueryParams()

    const url = `${apiUrl}/v2/recommended-price?${queryParams}`

    const {GENERIC} = this._config.get('COMMON').ERRORS

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getRecommendedPriceGenericSellerErrorFactory(
            '[HTTPSellerRepository#getRecommendedPriceGenericSellerError]'
          )
        }
      })
      .get(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        return this._recommendedPriceVOFactory(response.data)
      })
  }

  /**
   * @param {Object} params
   * @param {adEntity} params.adEntity
   * @param {userEntity} params.userEntity
   * @param {Boolean} params.simulate
   * @return {Promise}
   */
  @inlineError
  getInstantOfferByAd({adEntity, userEntity, simulate}) {
    const adId = adEntity.getId()
    const userId = userEntity.getId()

    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {VEHICLE_TYPE} = this._config.get('c2b')

    const queryParams = toQueryString({
      dealer_type: 'all',
      simulate: simulate ? true : undefined,
      unique_price: true,
      vehicle_type: VEHICLE_TYPE
    })

    const url = `${this._config.get(
      'API_URL'
    )}/v2/users/${userId}/classifieds/${adId}/instant-offers/appraisal?${queryParams}`

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => this._getInstantOfferGenericSellerErrorFactory(message),
        [NOT_FOUND]: ({message}) => this._getInstantOfferNotFoundSellerErrorFactory(message)
      })
      .get(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        if (response?.status === 204)
          return Promise.reject(
            this._getInstantOfferNotFoundSellerErrorFactory('[HTTPSellerRepository#getInstantOfferNotFoundError]')
          )
        return this._instantOfferSellerEntityFactory(response.data)
      })
  }

  /**
   * @param {Object} params
   * @param {instantOfferEntity} params.instantOfferEntity
   * @param {instantOfferRequest} params.instantOfferRequest
   * @param {userEntity} params.userEntity
   * @return {Promise}
   */
  @inlineError
  updateInstantOfferById({instantOfferEntity, instantOfferRequest, userEntity}) {
    const userId = userEntity.getId()
    const instantOfferId = instantOfferEntity.getId()
    const updateRequest = instantOfferRequest.getUpdateRequest()

    const url = `${this._config.get('API_URL')}/users/${userId}/instant-offers/${instantOfferId}`

    return this._authenticationFetcher
      .put(url, updateRequest)
      .then(() => Promise.resolve())
      .catch(() =>
        Promise.reject(this._getInstantOfferGenericSellerErrorFactory('HTTPSellerRepository#updateInstantOfferById'))
      )
  }

  @AsyncInlineError()
  async contactConfiguratorSeller({contactConfiguratorSellerRequest}) {
    const {HTTP_STATUS_CODES} = this._config.get('COMMON')
    const userId = contactConfiguratorSellerRequest.userId
    const body = contactConfiguratorSellerRequest.getBody()
    const url = `${this._config.get('API_URL')}/v1/users/${userId}/vehicle-specs/contact-message-request`
    try {
      const response = await this._authenticationFetcher.post(url, body)
      const {data} = response
      return ContactConfiguratorSellerResponseValueObject.createFromApi(data)
    } catch (error) {
      if (error.status === HTTP_STATUS_CODES.CONFLICT) {
        const {data} = error?.response
        return ContactConfiguratorSellerResponseValueObject.createFromApi(data)
      }
      return Promise.reject(this._contactModelSellerErrorFactory(error.message))
    }
  }

  @AsyncInlineError()
  async getLeadConfiguration({getLeadConfigurationRequest}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const makeId = getLeadConfigurationRequest.makeId
    const variationKey = getLeadConfigurationRequest.variationKey
    const url = `${this._config.get('API_URL')}/lead-configuration/make/${makeId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._contactModelSellerErrorFactory(message)
        }
      })
      .get(url)

    if (error) throw error

    if (response.status === NO_CONTENT) {
      return GetLeadConfigurationResponseValueObject.createDefault(variationKey)
    }

    const {data} = response

    return GetLeadConfigurationResponseValueObject.createFromApi(data, variationKey)
  }

  @AsyncInlineError()
  async contactConfiguratorNotLoggedSeller({contactConfiguratorNotLoggedSellerRequest}) {
    const {HTTP_STATUS_CODES} = this._config.get('COMMON')
    const body = contactConfiguratorNotLoggedSellerRequest.getBody()
    const url = `${this._config.get('API_URL')}/v1/vehicle-specs/contact-message-request/not-logged`
    try {
      const [error, response] = await this._fetchOrFail.post(url, body)

      if (error) throw error

      const {data} = response
      return ContactConfiguratorSellerResponseValueObject.createFromApi(data)
    } catch (error) {
      if (error.cause.status === HTTP_STATUS_CODES.CONFLICT && error.cause.response?.data) {
        const {data} = error.cause.response
        return ContactConfiguratorSellerResponseValueObject.createFromApi(data)
      }
      return Promise.reject(this._contactModelSellerErrorFactory(error.message))
    }
  }

  @AsyncInlineError()
  async saveInstantOfferDraftFromAPI({userId, request}) {
    const url = `${this._config.get('API_URL')}/users/${userId}/instant-offers/draft`
    const body = request.toJSON()

    const [error, response] = await this._fetchOrFail.put(url, body)

    if (error) throw error

    return response.data
  }

  @AsyncInlineError()
  async updateInstantOffer({instantOfferId, updateInstantOfferRequest}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_REQUEST, NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const url = `${this._config.get('API_URL')}/instant-offers/${instantOfferId}`
    const body = updateInstantOfferRequest.toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => this._getInstantOfferDetailsGenericSellerErrorFactory(message),
        [BAD_REQUEST]: ({message}) => this._getInstantOfferDetailsGenericSellerErrorFactory(message),
        [NOT_FOUND]: ({message}) => this._getInstantOfferDetailsNotFoundSellerErrorFactory(message)
      })
      .patch(url, body)

    if (error) throw error

    return InstantOfferDetailsSellerValueObject.createFromApi(response.data)
  }

  @AsyncInlineError()
  async updateInstantOfferPhone({instantOfferId, request}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const url = `${this._config.get('API_URL')}/instant-offers/${instantOfferId}/phone`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => new UpdateInstantOfferPhoneSellerError(message),
        [NOT_FOUND]: ({message}) => this._getInstantOfferNotFoundSellerErrorFactory(message)
      })
      .put(url, request.toJSON())

    if (error) throw error
  }

  @AsyncInlineError()
  async getDraftInstantOfferFromAPI({userEntity}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const userId = userEntity.getId()
    const url = `${this._config.get('API_URL')}/users/${userId}/instant-offers/draft`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () =>
          GetDraftInstantOfferFromAPISellerError.create({
            message: 'HTTPSellerRepository#getDraftInstantOfferFromAPI'
          })
      })
      .get(url)

    if (error) return Promise.reject(error)
    if (response.status === NO_CONTENT) return null
    return DraftInstantOfferFromAPISellerValueObject.create(response.data)
  }
}
